import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Locale } from "@dealer-center/ddc-core-utils";

/**
 * The tab order FR-005 fixes: User Information → Account Access → Feature Access → Login History.
 *
 * An `as const` tuple, never an `enum` (Configuration Principle III). The ids double as the `?tab=`
 * values, so they are the lowercase, URL-shaped names the prototype uses rather than the PascalCase
 * of the folders.
 */
export const MY_INFO_TAB_IDS = [
  "userinfo",
  "account",
  "feature",
  "login"
] as const;

export type MyInfoTabId = (typeof MY_INFO_TAB_IDS)[number];

/** Tab names are copy, so they live in `labels.json` (Configuration Principle VI). */
const TAB_LABEL_KEYS: Record<MyInfoTabId, string> = {
  userinfo: "USER_INFO",
  account: "ACCOUNT_ACCESS",
  feature: "FEATURE_ACCESS",
  login: "LOGIN_HISTORY"
};

export const MY_INFO_TAB_SEARCH_PARAM = "tab";

export interface MyInfoTab {
  id: MyInfoTabId;
  label: string;
}

export interface UseMyInfoTabsOptions {
  /**
   * Tabs whose availability has resolved to "does not apply" and must be omitted (FR-006).
   *
   * Only Account Access is ever conditional; the other three are always present. It is a parameter
   * rather than a query inside this hook for the reason research R-007 gives: `accountaccess` is the
   * slowest endpoint (~5–6s), and the tab must render **immediately in a loading state** and only
   * disappear once the query resolves — so the caller owns the query and passes the outcome down.
   * Nothing passes this yet; T045 wires it when the Account Access tab is built.
   */
  unavailableTabIds?: MyInfoTabId[];
}

export interface UseMyInfoTabs {
  /** Available tabs, in FR-005's order. */
  tabs: MyInfoTab[];
  activeTabId: MyInfoTabId;
  /** Records the selection in `?tab=`. Ignores anything that is not an available tab id. */
  selectTab: (tabId: string) => void;
}

const isMyInfoTabId = (value: string | null): value is MyInfoTabId =>
  MY_INFO_TAB_IDS.some(id => id === value);

/**
 * Owns the tab set, its order, and which one is selected.
 *
 * Selection lives in a `?tab=` search param rather than local state (research R-007): it makes a tab
 * shareable in a support thread and survives a refresh, at no routing cost. An unavailable or
 * misspelled value falls back to the first available tab rather than rendering an empty panel — the
 * bookmarked-Account-Access case for a user whose account has no hierarchy.
 *
 * There is deliberately **no** navigation guard or prompt here (FR-009). Nothing is editable in the
 * authorized build, so there is nothing to discard; more importantly, a guard added now is one Story
 * 2 would have to remove.
 */
export const useMyInfoTabs = ({
  unavailableTabIds = []
}: UseMyInfoTabsOptions = {}): UseMyInfoTabs => {
  const [searchParams, setSearchParams] = useSearchParams();

  const availableTabIds = MY_INFO_TAB_IDS.filter(
    id => !unavailableTabIds.includes(id)
  );

  const requestedTabId = searchParams.get(MY_INFO_TAB_SEARCH_PARAM);
  const activeTabId =
    availableTabIds.find(id => id === requestedTabId) ??
    availableTabIds[0] ??
    MY_INFO_TAB_IDS[0];

  const selectTab = useCallback(
    (tabId: string) => {
      if (!isMyInfoTabId(tabId)) {
        return;
      }

      const next = new URLSearchParams(searchParams);
      next.set(MY_INFO_TAB_SEARCH_PARAM, tabId);
      // `replace` so moving between tabs does not fill the history stack with entries the back
      // button then has to walk out of one tab at a time.
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return {
    tabs: availableTabIds.map(id => ({
      id,
      label: Locale.getText(TAB_LABEL_KEYS[id]) as string
    })),
    activeTabId,
    selectTab
  };
};
